import type {
  ApiDashboardElement,
  Collection,
  ReportStructure,
  TextFromEntityPropertyJSON,
} from '@highimpact-frontend/api-types';
import type { PlotTypes } from '@highimpact/dashboard-types';
import { useState } from 'react';
import { wait } from '../internal/utils/functions';
import * as R from '../internal/utils/record';
import {
  useDashboardLibraryDispatch,
  useDashboardLibrarySelector,
} from '../runtime/DashboardLibraryProvider';
import { hydrateDashboardLibraryState } from '../state/actions';
import {
  makeEmptyImage,
  makeEmptyImageGallery,
  makeEmptyLocalizationFromCoordinates,
  makeEmptyMap,
  makeEmptyPlot,
  makeEmptyRemoteWidget,
  makeEmptyTable,
  makeEmptyTableByCampaign,
  makeEmptyText,
  makeEmptyTextFromEntityProperty,
  REMOTE_WIDGET_AWAITING_CONFIGURATION,
  setAdditionalProperties,
  setElementEntityScopeFromWidgetEntity,
  ToRemoteWidgetParams,
} from '../state/dashboard-element-operations';
import { hasAvailableWidgetSlots } from '../state/dashboard-layout-operations';
import {
  setWidgetConfigPanelState,
  setWidgetFormIsOpen,
} from '../state/dashboard/slice';
import { buildMapping } from '../state/report-structure-operations';
import {
  addWidgetSafely,
  ReportDashboardDependencies,
} from '../state/store-operations';
import type { RootState } from '../state/types';

import type {
  DashboardGridType,
  WidgetFormOpen,
} from '@highimpact/dashboard-types';
import {
  CustomizedDialog,
  GenericErrorBoundary,
} from '@highimpact/dashboard-ui';
import ErrorIcon from '@mui/icons-material/Error';
import { Box, CircularProgress, Typography } from '@mui/material';
import {
  useDashboardHost,
  useDashboardScope,
} from '../context/DashboardHostContext';
import BuiltinGroupInitiatorForm from '../dashboard/widget/commons/forms/BuiltinGroupInitiatorForms';
import UnhandledForm from '../dashboard/widget/commons/forms/UnHandledForm';
import {
  getPluginGroupMfes,
  parsePluginGroupId,
} from '../dashboard/widget/widgetGroups';
import ImageForm from '../dashboard/widget/widgets/image/ImageForm';
import ImageGalleryForm from '../dashboard/widget/widgets/imageGallery/ImageGalleryForm';
import MapForm from '../dashboard/widget/widgets/map/MapForm';
import PlotInitiatorForm from '../dashboard/widget/widgets/plots/PlotInitiatorForm';
import PluginGroupInitiatorForm from '../dashboard/widget/widgets/remoteWidget/PluginGroupInitiatorForm';
import RemoteWidgetForm from '../dashboard/widget/widgets/remoteWidget/RemoteWidgetForm';
import ResourceWidgetInitiatorForm from '../dashboard/widget/widgets/resources/ResourceWidgetInitiatorForm';
import TableInitiatorForm from '../dashboard/widget/widgets/tables/TableInitiatorForm';
import TextFromEntityPropertyForm from '../dashboard/widget/widgets/textFromProperty/TextFromProperty';
import useMFEMetaData from '../internal/hooks/useMFEMetaData';
import { moveFirstMatchToFront } from '../internal/utils/array';
import {
  buildWidgetEntityFromHost,
  fetchSourceLabelMapForElement,
  pickMigratedFormProps,
  useCampaignsForSite,
} from './formHandlerShared';
import {
  applyInitiatorSeeding,
  catalogSupportsFullPathElements,
  partialPathElementTypes,
  resolveAvailableInitiatorElementTypes,
} from './initiatorCatalogSeeding';
import { useInitiatorCatalogBundle } from './useInitiatorCatalogBundle';

export type { PlotFormProps } from '../dashboard/widget/widgets/shared/types';

export {
  fullPathElementTypes,
  partialPathElementTypes,
} from './initiatorCatalogSeeding';

export const plotTypes: PlotTypes[] = [
  'timeseries',
  //'scatter',
  'windrose',
  'bar',
  'ombrothermic',
];

// -- Pretty names for the selectors in all forms

export const getElementPrettyName = (
  element_type: Exclude<
    ApiDashboardElement['element_type'],
    'collection' | 'readonlytext'
  >
): string => {
  switch (element_type) {
    case 'plot':
      return 'Graphique';
    case 'map':
      return 'Cartographie';
    case 'table':
      return 'Tableau de données';
    case 'tablebycampaign':
      return 'Tableau de campagnes';
    case 'text':
      return 'Zone de texte';
    case 'localizationfromcoordinates':
      return "Coordonnées d'une ressource";
    case 'textfromentityproperty':
      return "Propriété d'une ressource";
    case 'image':
      return 'Image';
    case 'remotewidget':
      return 'Plugins';
    case 'imagegallery':
      return "Galerie d'images";
  }
};

// -- Element type & pretty name mapping
export const elementTypeToPrettyName: Partial<
  Record<
    Exclude<ApiDashboardElement['element_type'], 'collection' | 'readonlytext'>,
    {
      label: string;
      formTitle: string;
      specificWidgetType?: string;
    }
  >
> = {
  plot: { label: 'Graphique', formTitle: 'Ajouter un graphique' },
  map: {
    label: 'Cartographie',
    formTitle: 'Ajouter une cartographie',
    specificWidgetType: 'map-viewer-react',
  },
  table: {
    label: 'Tableau de données',
    formTitle: 'Ajouter un tableau de données',
  },
  tablebycampaign: {
    label: 'Tableau de campagnes',
    formTitle: 'Ajouter un tableau de campagnes',
  },
  text: { label: 'Zone de texte', formTitle: 'Ajouter une zone de texte' },
  localizationfromcoordinates: {
    label: "Coordonnées d'une ressource",
    formTitle: "Ajouter des coordonnées d'une ressource",
  },
  // textfromentityproperty: {
  //   label: "Propriété d'une ressource",
  //   formTitle: "Ajouter une propriété d'une ressource",
  // },
  image: { label: 'Image', formTitle: 'Ajouter une image' },
  // remotewidget: { label: 'Plugin', formTitle: 'Ajouter un plugin' },
  imagegallery: {
    label: "Galerie d'images",
    formTitle: "Ajouter une galerie d'images",
  },
};

// -- Pretty names for the selectors in the plot form

export const getPlotTypePrettyName = (plot_type: PlotTypes): string => {
  switch (plot_type) {
    case 'timeseries':
      return 'Séries temporelles';
    case 'scatter':
      return 'Nuage de points';
    case 'bar':
      return 'Histogramme';
    case 'pie':
      return 'Camembert';
    case 'heatmap':
      return 'Carte thermique';
    case 'windrose':
      return 'Rose des vents';
    case 'geo':
      return 'Carte géographique';
    case 'ombrothermic':
      return 'Ombrothermie';
  }
};

// -- Pretty names for the selectors in the textfromentityproperty form

export const getResourcePrettyName = (
  entity_type: TextFromEntityPropertyJSON['entity_type']
): string => {
  switch (entity_type) {
    case 'project':
      return 'Projet';
    case 'site':
      return 'Site';
  }
};

const getProjectKeyPrettyName = (key: 'name'): string => {
  switch (key) {
    case 'name':
      return 'Nom';
  }
};

const getSiteKeyPrettyName = (key: 'name'): string => {
  switch (key) {
    case 'name':
      return 'Nom';
  }
};

export const getPropertyPrettyName = <
  T extends TextFromEntityPropertyJSON['entity_type']
>(
  entity_type: T,
  entity_property: string
): string => {
  switch (entity_type) {
    case 'project':
      return getProjectKeyPrettyName(entity_property as 'name');
    case 'site':
      return getSiteKeyPrettyName(entity_property as 'name');
    default:
      return entity_property;
  }
};

// -- Form

/**
 *
 * @description Logic of the WidgetConfigForm executed after calling the state decorator
 */
function InitWidgetFormHandlerHelper({
  configFormOpen,
  site_id,
  dashboard_id,
  dashboardData,
  store,
  gridType,
  startDate,
  endDate,
}: {
  configFormOpen: WidgetFormOpen;
  site_id: number | null;
  dashboard_id: number | null;
  dashboardData: DashboardGridType | undefined;
  store: RootState;
  gridType: 'reports' | 'grids';
  startDate: Date;
  endDate: Date;
}): JSX.Element {
  const dispatch = useDashboardLibraryDispatch();
  const host = useDashboardHost();
  const campaignsForSite = useCampaignsForSite(site_id);
  const catalogScope = {
    project_id: host.project_id ?? host.project?.id ?? null,
    site_id,
  };
  const {
    loading: catalogLoading,
    error: catalogError,
    bundle: initiatorCatalog,
  } = useInitiatorCatalogBundle(catalogScope);
  const [config, setConfig] = useState<Exclude<
    ApiDashboardElement,
    Collection
  > | null>(null);

  const mfeMetaData = useMFEMetaData();

  if (mfeMetaData.status === 'init' || mfeMetaData.status === 'loading') {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (mfeMetaData.status === 'error') {
    return <p>Récupération impossible des plugins</p>;
  }

  if (catalogLoading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (catalogError) {
    return <p>Récupération impossible des sources de données</p>;
  }

  const project_id = host.project_id ?? host.project?.id ?? null;

  if (!dashboard_id) {
    return <p>L'identifiant du tableau de bord n'a pas pu être récupéré</p>;
  }

  if (!dashboardData) {
    return <p>Les données du tableau de bord n'ont pas pu être récupérées</p>;
  }

  const reportStructure = dashboardData.params as ReportStructure | undefined;
  const reportDependencies: ReportDashboardDependencies | undefined =
    reportStructure && gridType === 'reports'
      ? {
          reportStructure,
          layouts: [],
          elementNameToVariableName: R.swapKeysAndValues(
            buildMapping(reportStructure)
          ),
        }
      : undefined;

  const pluginName = configFormOpen.widgetGroupId?.startsWith('plugin:')
    ? parsePluginGroupId(configFormOpen.widgetGroupId)
    : undefined;

  const relevantMfes =
    pluginName !== undefined
      ? getPluginGroupMfes(mfeMetaData.data, pluginName)
      : mfeMetaData.data;

  const mfe = moveFirstMatchToFront(
    relevantMfes,
    ({ name }) => name === configFormOpen.specificWidgetType
  );
  const widgetConfig: ToRemoteWidgetParams[] = (
    pluginName !== undefined ? relevantMfes : mfe
  ).map(({ name: specificWidgetType, url }) => ({ specificWidgetType, url }));

  const elementTypes = resolveAvailableInitiatorElementTypes(initiatorCatalog);
  const fullPathsAreAvailable =
    catalogSupportsFullPathElements(initiatorCatalog);

  const seedEntityScope = <T extends Exclude<ApiDashboardElement, Collection>>(
    element: T
  ): T => {
    const entity = buildWidgetEntityFromHost(host);
    return entity
      ? (setElementEntityScopeFromWidgetEntity(entity, element) as T)
      : element;
  };

  const buildInitialConfigFromElementType = (
    elementType: Exclude<
      ApiDashboardElement['element_type'],
      'collection' | 'readonlytext'
    >
  ) => {
    switch (elementType) {
      case 'plot':
        return makeEmptyPlot(
          {
            plot_type: 'timeseries',
            start_date: startDate.toISOString(),
            end_date: endDate.toISOString(),
          },
          true
        );

      case 'table':
        return makeEmptyTable(
          {
            start_date: startDate.toISOString(),
            end_date: endDate.toISOString(),
          },
          true
        );

      case 'tablebycampaign':
        return makeEmptyTableByCampaign(
          {
            start_date: startDate.toISOString(),
            end_date: endDate.toISOString(),
          },
          true
        );

      case 'map':
        return seedEntityScope(makeEmptyMap('site'));

      case 'text':
        return makeEmptyText(false);

      case 'textfromentityproperty':
        return makeEmptyTextFromEntityProperty({
          entity_type: 'project',
          entity_id: project_id ?? 0,
          entity_property: 'name',
        });

      case 'image':
        return seedEntityScope(makeEmptyImage());

      case 'imagegallery':
        return seedEntityScope(makeEmptyImageGallery());

      case 'localizationfromcoordinates':
        return makeEmptyLocalizationFromCoordinates({
          coordinate_source: 'site',
        });

      case 'remotewidget': {
        const firstPluginMfe =
          pluginName !== undefined
            ? getPluginGroupMfes(mfeMetaData.data, pluginName)[0]
            : mfe[0];
        if (firstPluginMfe) {
          return makeEmptyRemoteWidget(firstPluginMfe.name, firstPluginMfe.url);
        }
        // TODO: maybe an error should be thrown instead (with proper error handling)?
        return makeEmptyRemoteWidget('undefined', 'undefined');
      }

      default:
        return makeEmptyPlot(
          {
            plot_type: 'timeseries',
            start_date: startDate.toISOString(),
            end_date: endDate.toISOString(),
          },
          true
        );
    }
  };

  let initialConfig: ApiDashboardElement;

  initialConfig = buildInitialConfigFromElementType(configFormOpen.elementType);

  const elementType = configFormOpen.elementType as Exclude<
    ApiDashboardElement['element_type'],
    'collection' | 'readonlytext'
  >;
  const preserveUnconfigured = !['text', 'image', 'imagegallery'].includes(
    elementType
  );
  const seedingOptions = {
    bundle: initiatorCatalog,
    host,
    startDate,
    endDate,
    plugins: widgetConfig,
    preserveUnconfigured,
  };

  if (fullPathsAreAvailable) {
    initialConfig = applyInitiatorSeeding(
      elementType,
      initialConfig,
      seedingOptions
    );
  } else {
    const supportedPartialType = partialPathElementTypes.includes(elementType)
      ? elementType
      : 'text';
    initialConfig = applyInitiatorSeeding(
      supportedPartialType,
      initialConfig,
      seedingOptions
    );
  }

  const baseDep = {
    startDate,
    endDate,
    w: 12,
    h: 9,
  };

  const scrollToBottom = (
    totalDuration: number,
    safetyPixelInterval: number
  ) => {
    const gridElement = document.getElementsByClassName('sites-grid')[0];
    if (gridElement) {
      const SCROLLDURATION = 10;
      const scrollHeight = gridElement.scrollHeight;
      const scrollStep = scrollHeight / (totalDuration / SCROLLDURATION);

      const smoothScroll = () => {
        const scrollBarPosition =
          gridElement.scrollTop +
          gridElement.clientHeight +
          safetyPixelInterval;

        if (scrollBarPosition < gridElement.scrollHeight) {
          gridElement.scrollTop += scrollStep;
          wait(SCROLLDURATION, smoothScroll);
        }
      };

      smoothScroll();
    }
  };

  const onSubmit = async (config: Exclude<ApiDashboardElement, Collection>) => {
    const submittedElementType = config.element_type as Exclude<
      ApiDashboardElement['element_type'],
      'collection' | 'readonlytext'
    >;
    const preserveUnconfiguredForSubmit = ![
      'text',
      'image',
      'imagegallery',
    ].includes(submittedElementType);

    let configToSubmit = config;

    const shouldAddRemoteWidgetAwaitingConfiguration =
      configFormOpen.widgetGroupId?.startsWith('plugin:') &&
      submittedElementType === 'remotewidget';

    if (shouldAddRemoteWidgetAwaitingConfiguration) {
      configToSubmit = setAdditionalProperties(
        REMOTE_WIDGET_AWAITING_CONFIGURATION,
        config
      ) as Exclude<ApiDashboardElement, Collection>;
    }

    if (
      dashboardData?.additionalProperties?.layoutType === 'svg-constrained' &&
      !hasAvailableWidgetSlots(
        dashboardData.additionalProperties.widgetSlot,
        Object.keys(dashboardData.widgets).length
      )
    ) {
      return;
    }

    try {
      const widgetId = await host.api.dashboard.postDashboardElement(
        dashboard_id,
        configToSubmit
      );

      const sourceLabelMap = await fetchSourceLabelMapForElement(
        configToSubmit,
        host.api.sources.getLabels,
        {
          project_id: project_id ?? undefined,
          site_id: site_id ?? undefined,
        }
      );
      const dep = {
        ...baseDep,
        sourceLabelMap,
      };

      const newState =
        gridType === 'reports'
          ? addWidgetSafely(
              {
                ...dep,
                ...(reportDependencies as ReportDashboardDependencies),
                posistioningStrategy: 'bottom',
              },
              dashboard_id,
              gridType,
              { ...configToSubmit, id: widgetId },
              store
            )
          : addWidgetSafely(
              { ...dep, posistioningStrategy: 'bottom' },
              dashboard_id,
              gridType,
              { ...configToSubmit, id: widgetId },
              store
            );
      dispatch(hydrateDashboardLibraryState(newState));
      dispatch(setWidgetFormIsOpen({ state: 'closed' }));
      if (preserveUnconfiguredForSubmit) {
        dispatch(
          setWidgetConfigPanelState({
            state: 'Configuring',
            elementType: submittedElementType,
            gridId: dashboard_id,
            gridType: gridType,
            widgetId,
          })
        );
      }
      setConfig(null);
      scrollToBottom(500, 3);
    } catch {
      dispatch(setWidgetFormIsOpen({ state: 'closed' }));
    }
  };

  const onChange = (config: Exclude<ApiDashboardElement, Collection>) => {
    setConfig(config);
  };

  const formProps = {
    mfeMetaData: mfeMetaData.data,
    config: config || initialConfig,
    project_id,
    site_id,
    campaigns: campaignsForSite,
    start_time: startDate,
    end_time: endDate,
    elementTypes,
    onChange: onChange as (config: ApiDashboardElement) => void,
    onSubmit: onSubmit as (config: ApiDashboardElement) => Promise<void | {
      payload: boolean;
      type: 'dashboard-slice/setWidgetFormIsOpen';
    }>,
  };
  const migratedFormProps = pickMigratedFormProps(formProps);

  const groupWidgets = Object.fromEntries(
    Object.entries(elementTypeToPrettyName).flatMap(([key, value]) =>
      value ? [[key, { label: value.label, formTitle: value.formTitle }]] : []
    )
  );

  // --- build the inner form element
  let formElement: JSX.Element;
  const shouldUseGenericInitiatorForm = Boolean(configFormOpen.widgetGroupId);
  const widgetGroupId = configFormOpen.widgetGroupId;
  const isPluginWidgetGroup = widgetGroupId?.startsWith('plugin:');

  if (shouldUseGenericInitiatorForm) {
    if (isPluginWidgetGroup) {
      formElement = (
        <PluginGroupInitiatorForm {...formProps} pluginMfes={relevantMfes} />
      );
    } else {
      formElement = (
        <BuiltinGroupInitiatorForm
          {...formProps}
          // shouldUseGenericInitiatorForm === true => widgetGroupId is defined
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          groupId={configFormOpen.widgetGroupId!}
          widgets={groupWidgets}
        />
      );
    }
  } else {
    switch (initialConfig.element_type) {
      case 'plot':
        formElement = <PlotInitiatorForm {...migratedFormProps} />;
        break;
      case 'map':
        formElement = <MapForm {...migratedFormProps} />;
        break;
      case 'table':
        formElement = <TableInitiatorForm {...migratedFormProps} />;
        break;
      case 'tablebycampaign':
        formElement = <TableInitiatorForm {...migratedFormProps} />;
        break;
      case 'text':
        formElement = <ResourceWidgetInitiatorForm {...formProps} />;
        break;
      case 'localizationfromcoordinates':
        formElement = <UnhandledForm />;
        break;
      case 'textfromentityproperty':
        formElement = <TextFromEntityPropertyForm {...migratedFormProps} />;
        break;
      case 'image':
        formElement = <ImageForm {...migratedFormProps} />;
        break;
      case 'remotewidget':
        formElement = (
          <RemoteWidgetForm {...migratedFormProps} mfeMetaData={mfe} />
        );
        break;
      case 'imagegallery':
        formElement = <ImageGalleryForm {...migratedFormProps} />;
        break;
      default:
        formElement = <UnhandledForm />;
        break;
    }
  }

  // --- return the inner form element (dialog shell is owned by InitWidgetFormHandler)
  return formElement;
}

interface InitWidgetFormHandlerProps {
  gridType: 'grids' | 'reports';
  //Props for CustomizedDialog
  title?: string;
  isOpen?: boolean;
  withOpenButton?: boolean;
  onCancel?: () => void;
  onSave?: () => void;
  grid_id?: number;
}

function InitWidgetFormHandler({
  gridType,
  title,
  onCancel,
  ...props
}: InitWidgetFormHandlerProps) {
  const dispatch = useDashboardLibraryDispatch();
  const scope = useDashboardScope();
  const site_id = scope.site_id;
  const dashboard_id = props.grid_id ?? scope.grid_id;

  // default date values: from today at midnight to tomorrow at midnight
  const startDate = new Date();
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(startDate);
  endDate.setDate(endDate.getDate() + 1);

  const dashboardData = useDashboardLibrarySelector((state) =>
    dashboard_id === null ? undefined : state.dashboard[gridType][dashboard_id]
  );

  const store = useDashboardLibrarySelector((state) => state);

  const widgetFormOpen = useDashboardLibrarySelector(
    (state) => state.dashboard.widgetFormIsOpen
  );

  if (widgetFormOpen.state === 'closed') {
    return null;
  }

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
      return;
    }
    dispatch(setWidgetFormIsOpen({ state: 'closed' }));
  };

  const dialogTitle = title ?? widgetFormOpen.formTitle ?? 'Ajouter un élément';
  const formBoundaryResetKey = `${widgetFormOpen.elementType}:${
    widgetFormOpen.widgetGroupId ?? 'standalone'
  }`;

  return (
    <CustomizedDialog open onCancel={handleCancel} title={dialogTitle}>
      <GenericErrorBoundary
        resetKey={formBoundaryResetKey}
        fallback={
          <Typography component="h4" align="center">
            <ErrorIcon /> Impossible d'afficher le formulaire
          </Typography>
        }
      >
        <InitWidgetFormHandlerHelper
          configFormOpen={widgetFormOpen}
          site_id={site_id}
          dashboard_id={dashboard_id}
          dashboardData={dashboardData}
          store={store}
          gridType={gridType}
          startDate={startDate}
          endDate={endDate}
        />
      </GenericErrorBoundary>
    </CustomizedDialog>
  );
}

export default InitWidgetFormHandler;
